import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  Input,
  Label,
  LayerCard,
  Select,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { BuildingsIcon, HouseIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { Building, WifiNetwork } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  DomainTagInput                                                     */
/* ------------------------------------------------------------------ */

interface DomainTagInputProps {
  value: string[]
  onChange: (domains: string[]) => void
  placeholder?: string
}

function DomainTagInput({ value, onChange, placeholder }: DomainTagInputProps) {
  const [input, setInput] = useState('')

  function addDomain(raw: string) {
    const domain = raw.trim().toLowerCase()
    if (domain && !value.includes(domain)) {
      onChange([...value, domain])
    }
    setInput('')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addDomain(input)
    }
    if (e.key === 'Backspace' && input === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  function removeDomain(domain: string) {
    onChange(value.filter((d) => d !== domain))
  }

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-kumo-ring bg-kumo-base px-2 py-1.5">
      {value.map((domain) => (
        <span
          key={domain}
          className="inline-flex items-center gap-1 rounded-md bg-kumo-tint px-2 py-0.5 text-xs text-kumo-default"
        >
          {domain}
          <button
            type="button"
            onClick={() => removeDomain(domain)}
            className="ml-0.5 text-kumo-muted hover:text-kumo-default"
            aria-label={`Remove ${domain}`}
          >
            &times;
          </button>
        </span>
      ))}
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (input.trim()) addDomain(input)
        }}
        placeholder={value.length === 0 ? placeholder : ''}
        className="min-w-[120px] flex-1 border-none bg-transparent text-sm outline-none"
      />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  CompanyFormPage                                                     */
/* ------------------------------------------------------------------ */

interface CompanyFormData {
  name: string
  taxId: string
  domains: string[]
  email: string
  planType: string
  wifiNetworkIds: string[]
  buildingId: string
}

const emptyForm: CompanyFormData = {
  name: '',
  taxId: '',
  domains: [],
  email: '',
  planType: '',
  wifiNetworkIds: [],
  buildingId: '',
}

interface CompanyResponse {
  id: string
  name: string
  taxId: string
  domains: string[] | null
  email: string
  planType: string
  wifiNetworkIds: string[] | null
  buildingId?: string
}

export function CompanyFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [form, setForm] = useState<CompanyFormData>(emptyForm)
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [allNetworks, setAllNetworks] = useState<WifiNetwork[]>([])
  const [allBuildings, setAllBuildings] = useState<Building[]>([])

  useEffect(() => {
    async function fetchData() {
      try {
        const token = await getAccessTokenSilently()

        const fetches: Promise<unknown>[] = [
          apiRequest<WifiNetwork[]>('/api/companies/networks', { token }).then(setAllNetworks),
          apiRequest<Building[]>('/api/buildings', { token }).then(setAllBuildings),
        ]

        if (id) {
          fetches.push(
            apiRequest<CompanyResponse>(`/api/companies/${id}`, { token }).then((company) => {
              setForm({
                name: company.name,
                taxId: company.taxId,
                domains: company.domains ?? [],
                email: company.email,
                planType: company.planType,
                wifiNetworkIds: company.wifiNetworkIds ?? [],
                buildingId: company.buildingId ?? '',
              })
            }),
          )
        }

        await Promise.all(fetches)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [id, getAccessTokenSilently, t])

  function setField<K extends keyof CompanyFormData>(key: K, value: CompanyFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      const token = await getAccessTokenSilently()

      if (isEdit) {
        await apiRequest(`/api/companies/${id}`, {
          method: 'PUT',
          token,
          body: form,
        })
        toasts.add({ variant: 'success', title: t('companies.updateSuccess') })
      } else {
        await apiRequest('/api/companies', {
          method: 'POST',
          token,
          body: form,
        })
        toasts.add({ variant: 'success', title: t('companies.createSuccess') })
      }
      navigate('/companies')
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/companies">
              {t('companies.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>
              {isEdit ? t('companies.editCompany') : t('companies.createCompany')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={isEdit ? t('companies.editCompany') : t('companies.createCompany')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && (
        <LayerCard>
          <LayerCard.Primary>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Label>{t('companies.name')}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('companies.taxId')}</Label>
                <Input
                  value={form.taxId}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '')
                    setField('taxId', digits)
                  }}
                  placeholder={t('companies.taxIdHint')}
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('companies.domains')}</Label>
                <DomainTagInput
                  value={form.domains}
                  onChange={(domains) => setField('domains', domains)}
                  placeholder={t('companies.domainPlaceholder')}
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('companies.email')}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('companies.planType')}</Label>
                <Input
                  value={form.planType}
                  onChange={(e) => setField('planType', e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('companies.building')}</Label>
                <Select
                  value={form.buildingId || ''}
                  onValueChange={(v) => {
                    setField('buildingId', v ?? '')
                  }}
                >
                  <Select.Option value="">{t('companies.selectBuilding')}</Select.Option>
                  {allBuildings.map((building) => (
                    <Select.Option key={building.id} value={building.id}>
                      {building.name} - {building.city}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('companies.wifiNetworks')}</Label>
                {allNetworks.length === 0 ? (
                  <Text as="p" variant="secondary" size="sm">
                    {t('companies.noNetworksAvailable')}
                  </Text>
                ) : (
                  <div className="flex flex-col gap-2">
                    {allNetworks.map((network) => (
                      <label key={network.id} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.wifiNetworkIds.includes(network.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm({
                                ...form,
                                wifiNetworkIds: [...form.wifiNetworkIds, network.id],
                              })
                            } else {
                              setForm({
                                ...form,
                                wifiNetworkIds: form.wifiNetworkIds.filter(
                                  (nid) => nid !== network.id,
                                ),
                              })
                            }
                          }}
                        />
                        <span className="text-sm">
                          {network.name} — {network.securityType}
                          {network.vlanId ? ` · VLAN ${network.vlanId}` : ''}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => navigate('/companies')}>
                  {t('companies.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={submitting}>
                  {t('companies.save')}
                </Button>
              </div>
            </form>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
